/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
/**
 * M19 Оценка вероятности погашения долга по правилам и баллам (при малом объёме собственных данных лицензиата).
 * Каждый фактор даёт поправку к базовой оценке; итог 0-100 %. Факторы показываются пользователю.
 */
export interface ScoreInput {
  debtorKind: 'LEGAL' | 'IP' | 'PERSON' | string;
  outstanding: number;            // остаток долга, руб.
  principal: number;
  daysOverdue: number | null;     // дней с даты, когда долг должен был быть погашен
  limitationExpired: boolean;
  paymentsCount: number;          // частичные оплаты по долгу
  promisesKept: number; promisesBroken: number;
  egrulStatus?: string | null;    // ACTIVE | LIQUIDATING | LIQUIDATED | BANKRUPT ...
  bankruptcyCase: boolean;
  revenue?: number | null; netAssets?: number | null; netProfit?: number | null;
  otherDebtsCount: number;        // другие долги этого должника в базе
  courtCasesAsDefendant: number;
  writsReturned: number;          // ИЛ возвращены (невозможно взыскать)
  stage: string;
}
export interface ScoreFactor { name: string; impact: number; note: string }
export interface ScoreResult { score: number; expected: number; factors: ScoreFactor[]; strategy: string; strategyCode: string }

export const STRATEGY: Record<string, string> = {
  PRETRIAL: 'Досудебное урегулирование: претензия, звонок, график погашения',
  SETTLEMENT: 'Мировое соглашение или рассрочка',
  COURT: 'Суд или приказное производство, затем исполнительный лист',
  BANKRUPTCY: 'Требование в деле о банкротстве (включение в реестр)',
  CESSION: 'Уступка требования (цессия)',
  WRITEOFF: 'Списание как безнадёжного долга',
};

export function scoreDebt(i: ScoreInput): ScoreResult {
  const f: ScoreFactor[] = [];
  const add = (name: string, impact: number, note: string) => { if (impact) f.push({ name, impact, note }); };
  let base = i.debtorKind === 'PERSON' ? 45 : 50;
  add('Вид должника', i.debtorKind === 'PERSON' ? -5 : 0, i.debtorKind === 'PERSON' ? 'Гражданин: взыскание дольше, имущественный иммунитет' : '');
  if (i.daysOverdue != null) {
    if (i.daysOverdue <= 30) add('Срок просрочки', 15, 'До 30 дней - высокая вероятность добровольной оплаты');
    else if (i.daysOverdue <= 180) add('Срок просрочки', 5, 'До полугода');
    else if (i.daysOverdue <= 365) add('Срок просрочки', -5, 'От полугода до года');
    else add('Срок просрочки', -15, 'Больше года');
  }
  if (i.limitationExpired) add('Исковая давность', -35, 'Срок исковой давности истёк: взыскать в суде можно только при признании долга');
  if (i.paymentsCount > 0) add('Частичные оплаты', Math.min(20, 8 + i.paymentsCount * 4), `Должник платил ${i.paymentsCount} раз(а)`);
  if (i.promisesKept) add('Выполненные обещания', Math.min(10, i.promisesKept * 5), `Обещания выполнены: ${i.promisesKept}`);
  if (i.promisesBroken) add('Невыполненные обещания', -Math.min(15, i.promisesBroken * 5), `Обещания не выполнены: ${i.promisesBroken}`);
  const st = (i.egrulStatus ?? '').toUpperCase();
  if (st && st !== 'ACTIVE') add('Статус в ЕГРЮЛ', st.includes('BANKRUPT') ? -30 : st.includes('LIQUIDAT') ? -25 : -15, `Статус: ${i.egrulStatus}`);
  if (i.bankruptcyCase) add('Банкротство', -25, 'Есть дело о банкротстве: удовлетворение только через реестр требований');
  if (i.revenue != null) {
    if (i.revenue <= 0) add('Выручка', -10, 'Нулевая выручка в последней отчётности');
    else if (i.revenue > i.outstanding * 20) add('Выручка', 10, 'Выручка многократно больше долга');
  }
  if (i.netAssets != null && i.netAssets < 0) add('Чистые активы', -10, 'Отрицательные чистые активы');
  if (i.netProfit != null && i.netProfit > 0) add('Прибыль', 5, 'Прибыль в последнем году');
  if (i.otherDebtsCount) add('Другие долги', -Math.min(15, i.otherDebtsCount * 5), `Ещё долгов этого должника в базе: ${i.otherDebtsCount}`);
  if (i.courtCasesAsDefendant > 2) add('Судебная нагрузка', -10, `Должник - ответчик по ${i.courtCasesAsDefendant} делам`);
  if (i.writsReturned) add('Возвращённые ИЛ', -15, 'Исполнительные документы возвращены без исполнения');
  if (i.stage === 'ENFORCEMENT' || i.stage === 'WRIT') add('Стадия', 5, 'Есть исполнительный документ');
  const score = Math.max(1, Math.min(99, Math.round(base + f.reduce((s, x) => s + x.impact, 0))));
  const expected = Math.round(i.outstanding * score) / 100;
  let code = 'COURT';
  if (i.bankruptcyCase || st.includes('BANKRUPT')) code = 'BANKRUPTCY';
  else if (i.limitationExpired || (score < 15 && i.outstanding < 100_000)) code = score < 15 ? 'WRITEOFF' : 'CESSION';
  else if (score < 25) code = 'CESSION';
  else if (score >= 65 && (i.daysOverdue ?? 0) <= 180) code = 'PRETRIAL';
  else if (score >= 50 && i.paymentsCount > 0) code = 'SETTLEMENT';
  return { score, expected, factors: f, strategy: STRATEGY[code], strategyCode: code };
}
